// Anropen för modulen Inköp. Backend: backend/routers/inkop.py.
import api from './client';

export type InköpStatus = 'dialog' | 'bekraftat' | 'pagar' | 'inlevererat' | 'avslutat' | 'avbrutet';
export type LeveransStatus = 'planerad' | 'bokad' | 'pa_vag' | 'inlevererad';
export type KostnadsTyp = 'frakt' | 'agent' | 'tull' | 'speditor' | 'ovrigt';

export const STATUSNAMN: Record<InköpStatus, string> = {
  dialog: 'I dialog', bekraftat: 'Bekräftat', pagar: 'På väg',
  inlevererat: 'Inlevererat', avslutat: 'Avslutat', avbrutet: 'Avbrutet',
};
export const STATUSORDNING: InköpStatus[] = ['dialog', 'bekraftat', 'pagar', 'inlevererat', 'avslutat'];
// Ett inköp som pågår syns som Aktuellt inköp på producentkortet.
export const AKTIVA: InköpStatus[] = ['dialog', 'bekraftat', 'pagar', 'inlevererat'];

// Kandidatens steg. Inköp startat, Vilande och Avstått är beslut.
export type KandidatSteg = 'kontakt' | 'prover' | 'provade' | 'inkop' | 'vilande' | 'avstatt';
export const STEGNAMN: Record<KandidatSteg, string> = {
  kontakt: 'Kontakt', prover: 'Prover på väg', provade: 'Provade',
  inkop: 'Inköp startat', vilande: 'Vilande', avstatt: 'Avstått',
};
export type Omdömesval = 'ja' | 'kanske' | 'nej';
export const OMDÖMESNAMN: Record<Omdömesval, string> = { ja: 'Ja', kanske: 'Kanske', nej: 'Nej' };
export const LEVERANSNAMN: Record<LeveransStatus, string> = {
  planerad: 'Planerad', bokad: 'Bokad', pa_vag: 'På väg', inlevererad: 'Inlevererad',
};
export const KOSTNADSNAMN: Record<KostnadsTyp, string> = {
  frakt: 'Frakt till lager', agent: 'Agent', tull: 'Tull', speditor: 'Speditör', ovrigt: 'Övrigt',
};

export interface Kategori { id: string; namn: string; farg: string }
export interface Kontakt { id: string; namn: string; roll?: string | null; epost?: string | null; telefon?: string | null; sprak?: string | null }
// Varifrån dagarna räknas. forsta_betalning: som Boissons 33/33/33, där andra och
// tredje delen räknas från den första betalningen och inte från leveransen.
export type Villkorsbas = 'bestallning' | 'leverans' | 'ankomst' | 'forsta_betalning';
export interface Villkor { andel: number; dagar: number; fran: Villkorsbas }
export const VILLKORSBAS: Record<Villkorsbas, string> = {
  bestallning: 'beställning', leverans: 'hämtning', ankomst: 'ankomst till Sverige', forsta_betalning: 'första betalningen',
};
// Hela procent som alltid blir 100 tillsammans (33, 33, 34 i stället för 33, 33, 33).
function procentdelar(v: Villkor[]): number[] {
  const delar = v.map(x => Math.round(x.andel * 100));
  const summa = delar.reduce((a, b) => a + b, 0);
  if (delar.length && Math.abs(summa - 100) <= 2) delar[delar.length - 1] += 100 - summa;
  return delar;
}
// En del per rad: "33 % vid beställning", "33 % 30 dagar efter första betalningen".
export function villkorDelar(v?: Villkor[] | null): string[] {
  if (!v?.length) return [];
  const p = procentdelar(v);
  return v.map((x, n) => {
    const bas = VILLKORSBAS[x.fran] || x.fran;
    return `${p[n]} % ${x.dagar ? `${x.dagar} dagar efter ${bas}` : `vid ${bas}`}`;
  });
}
export function villkorText(v?: Villkor[] | null): string {
  return villkorDelar(v).join(', ') || 'Inga villkor angivna';
}
export type Mottagare = 'kvh' | 'kran_aps';
export const MOTTAGARNAMN: Record<Mottagare, string> = { kvh: 'KRAN Vinhandel', kran_aps: 'KRAN ApS' };
export type Palltyp = 'hel' | 'halv' | 'vmf';
export const PALLNAMN: Record<Palltyp, string> = { hel: 'Helpall', halv: 'Halvpall', vmf: 'VMF-pall' };
export interface Person { id: string; namn: string }

export interface Producent {
  id: string;
  namn: string;
  land?: string | null;
  region?: string | null;
  webbplats?: string | null;
  valuta: string;
  agent_andel: number;
  betalningsvillkor: Villkor[];
  producentansvarig?: string | null;
  logistikansvarig?: string | null;
  fortnox_namn: string[];
  // Leverantörsnummer i Fortnox; deras fakturor är betalningarna till producenten.
  fortnox_leverantorer?: string[];
  domaner: string[];
  anteckning?: string | null;
  aktiv: boolean;
  kategorier: Kategori[];
  max_flaskor_per_pall?: number | null;
  adress?: string | null;
  antal_inkop?: number;
  pagaende?: number;
  senaste_inkop?: string | null;
  // Kandidat: ännu inga artiklar i Fortnox. Blir producent av sig själv när de finns.
  kandidat?: boolean;
  blev_producent?: string | null;
  kandidat_steg?: KandidatSteg;
  vilande_till?: string | null;
  steg_anledning?: string | null;
}

export interface Händelse {
  id: string;
  inkop_id?: string | null;
  typ: 'mejl' | 'anteckning' | 'besok' | 'prov' | 'steg';
  datum: string;
  rubrik?: string | null;
  sammanfattning?: string | null;
  avsandare?: string | null;
  utdrag?: { fran?: KandidatSteg; till?: KandidatSteg; automatiskt?: boolean } | null;
  skapad_av?: string | null;
  skapad_av_namn?: string | null;
  skapad?: string | null;
}

export interface ProvOmdöme {
  id: string; prov_id: string; anvandare: string; namn?: string | null;
  omdome: Omdömesval; kommentar?: string | null; datum: string;
}
export interface Prov {
  id: string;
  vin: string;
  argang?: string | null;
  antal_flaskor?: number | null;
  mottaget?: string | null;
  mottaget_av_namn?: string | null;
  anteckning?: string | null;
  skapad: string;
  skapad_av_namn?: string | null;
  omdomen: ProvOmdöme[];
}
export interface Kundprovning {
  provning_id: string; vin?: string | null; reaktion?: string | null; kommentar?: string | null;
  datum?: string | null; kund_id?: string | null; kund?: string | null; saljare?: string | null;
}
// Samma vin provat hos flera kunder, sammanslaget trots olika stavning i mejlen.
export interface Kundvin {
  vin: string; antal: number; gillade: number; reaktioner: Record<string, number>;
  senaste_datum?: string | null; provningar: Kundprovning[];
}

export interface ProducentVin {
  artikel_nr: string;
  namn: string;
  listpris?: number | null;
  lager?: number | null;
  salt_12m: number;
  senast_sald?: string | null;
}

export interface InköpKort {
  id: string;
  producent_id: string;
  producent_namn?: string;
  rubrik?: string | null;
  status: InköpStatus;
  statusnamn: string;
  valuta: string;
  kurs?: number | null;
  bestalld?: string | null;
  skapad: string;
  uppdaterad: string;
  flaskor: number;
  belopp_valuta: number;
  belopp_kr?: number | null;
  importerat: boolean;
  producentansvarig?: string | null;
  logistikansvarig?: string | null;
  // Fakturorna i Fortnox som hör till inköpet (bara på producentkortet).
  fakturor?: { antal: number; obetalda: number; obetalt_kr: number };
  // Innan raderna finns: belopp och flaskor är uppskattade.
  uppskattat?: boolean;
  uppskattat_belopp?: number | null;
  uppskattade_flaskor?: number | null;
  senast_i_sverige?: string | null;
  kran_vin_datum?: string | null;
  kran_aps_flaskor?: number;
  kran_aps_belopp_valuta?: number;
  // Risk att inte hinna fram till det hårda datumet.
  varning?: string | null;
  kurs_info?: KursInfo;
}

// Kursen ett inköp räknas med. Pågående inköp följer dagens kurs från
// Riksbanken tills någon skriver in en egen; beloppen i kronor är då preliminära.
export interface KursInfo {
  kurs: number | null;
  kalla: 'sek' | 'egen' | 'riksbanken' | 'inkopet' | 'saknas';
  datum?: string | null;
  dagens?: { kurs: number; datum: string } | null;
}

export interface ProducentDetalj extends Producent {
  kontakter: Kontakt[];
  handelser: Händelse[];
  inkop: InköpKort[];
  viner: ProducentVin[];
  prover: Prov[];
  kundprovningar: Kundvin[];
  jag: Person;
  // Leverantörer i Fortnox: de kopplade, och de okopplade som liknar producenten mest först.
  leverantorer: { kopplade: FortnoxLeverantör[]; forslag: (FortnoxLeverantör & { poang: number })[]; hamtade: boolean };
}
export interface FortnoxLeverantör { nr: string; namn?: string | null; antal: number; valutor: string[]; senaste?: string | null }

export interface Kalkyl {
  pris_kr: number; frakt_till_lager: number; agent: number; ls: number;
  lagerkostnad: number; lagerkostnad_rad: number; punktskatt: number;
  frakt_till_kund: number; ovrigt: number;
  // Prissättningen: totalt inpris, bokföringens inpris (utan punktskatt, frakt till kund och LS),
  // förslag till listpris med 29,5 % marginal och marginalen (listpris − totalt inpris) / listpris.
  inpris_totalt: number; inpris_bokforing: number; forslag_listpris: number | null; marginal_pct: number | null;
  agent_andel: number; kurs: number;
  nettointakt: number | null; bruttomarginal: number | null; bruttomarginal_pct: number | null;
  tackningsbidrag: number | null; tackningsbidrag_pct: number | null; vinst_rad: number | null;
}

export interface Rad {
  id: string;
  ordning: number;
  vin: string;
  argang?: string | null;
  volym_cl: number;
  alkoholhalt?: number | null;
  antal: number;
  pris_valuta?: number | null;
  listpris?: number | null;
  artikel_nr?: string | null;
  forpackning?: number | null;
  anteckning?: string | null;
  mottagare: Mottagare;
  reserverad_for?: string | null;
  prov: boolean;
  ordinarie_pris_valuta?: number | null;
  kalkyl: Kalkyl;
  // Listpriset i Fortnox är sanningen. listpris ovan är bara ett planerat pris
  // för viner som inte finns i Fortnox än. Kalkylen räknas på listpris_galler.
  fortnox_artikel_nr?: string | null;
  listpris_fortnox?: number | null;
  listpris_galler?: number | null;
  listpris_kalla?: 'fortnox' | 'planerat' | null;
  // Kalkylen per vin i Prissättning, som i Excel; tomt = inköpets eller standardens värde.
  frakt_per_flaska?: number | null; agent_andel?: number | null; ls?: number | null; frakt_till_kund?: number | null;
  ovrigt?: number | null; punktskatt?: number | null; provpris?: number | null;
}

// Prissättning: vinerna i inköpen med kalkyl och förslag till listpris.
// Samma vin i ett tidigare inköp. Listpriset i Fortnox är det som gällde; Excel bara som jämförelse.
export interface Tidigare {
  inkop_id: string; datum: string; argang?: string | null; vin?: string; pris_valuta?: number | null; valuta: string;
  inpris_totalt: number; listpris_fortnox?: number | null; artikel_nr?: string | null; marginal_pct?: number | null;
  listpris_excel?: number | null; last_listpris?: number | null;
}
export type PrisRad = Rad & {
  tidigare?: Tidigare[]; listpris_excel?: number | null;
  // Det låsta priset när beställningen låstes; står kvar som historik.
  last_listpris?: number | null; last_inpris?: number | null; last_marginal?: number | null; last_tid?: string | null;
  last_kalkyl?: { kurs?: number | null; [nyckel: string]: number | null | undefined } | null;
};
export type Vintyp = 'rott' | 'vitt' | 'rose' | 'orange' | 'mousserande' | 'sott' | 'annat';
export const VINTYPNAMN: Record<Vintyp, string> = {
  rott: 'Rött', vitt: 'Vitt', rose: 'Rosé', orange: 'Orange', mousserande: 'Mousserande', sott: 'Sött', annat: 'Annat',
};
// Ett vin att räkna på innan något inköp finns. provpris är bara ett räknepris.
export interface Prisutkast {
  id: string; producent_id: string; vin: string; typ?: Vintyp | null; argang?: string | null; volym_cl: number;
  alkoholhalt?: number | null; pris_valuta?: number | null; antal?: number | null; frakt_per_flaska?: number | null;
  provpris?: number | null; anteckning?: string | null; kalkyl: Kalkyl; frakt_anvand: number; tidigare?: Tidigare[];
  agent_andel?: number | null; ls?: number | null; frakt_till_kund?: number | null; ovrigt?: number | null;
  punktskatt?: number | null; kurs?: number | null;
}
export interface PrisInköp {
  id: string; producent_id: string; producent: string; status: InköpStatus; statusnamn: string;
  rubrik?: string | null; bestalld?: string | null; skapad?: string; valuta: string; kurs?: number | null;
  kurs_info?: KursInfo; rader: PrisRad[]; utan_listpris: number; under_mal: number;
  datum: string; importerat: boolean; import_kalla?: string | null;
  priser_last?: string | null; priser_last_av?: string | null;
}
export interface PrisProducent {
  id: string; namn: string; land: string; region?: string | null; valuta: string; kandidat: boolean; aktiv: boolean;
  kurs_info?: KursInfo; agent_andel: number; frakt_forslag?: number | null;
  pagaende: PrisInköp[]; utkast: Prisutkast[]; historik: PrisInköp[];
}
export interface AttLäggaUpp {
  rad_id: string; inkop_id: string; producent_id: string; producent: string; vin: string; argang?: string | null;
  volym_cl?: number | null; alkoholhalt?: number | null; listpris: number; inpris_bokforing: number; angivet_artikel_nr?: string | null;
  antal?: number | null; inpris?: number | null; kommit: boolean; status: InköpStatus; statusnamn?: string | null; last?: string | null;
}
export interface Punktskattesats { till_alkohol: number; kr_per_liter: number; namn: string; per_ren_alkohol?: boolean }
export interface Prissättning {
  malmarginal: number; foreslagen_marginal: number; standard: { ls: number; frakt_till_kund: number; ovrigt: number; frakt_till_lager?: number; foreslagen_marginal?: number; lagergrans?: number };
  punktskatt?: Punktskattesats[];
  producenter: PrisProducent[]; att_lagga_upp: AttLäggaUpp[];
}
export const hämtaPrissättning = () => api.get<Prissättning>('/inkop/prissattning');
export const justeraLåstListpris = (radId: string, listpris: number) => api.patch(`/inkop/prissattning/rad/${radId}/listpris`, { listpris });
export const sparaInställningar = (data: { kalkyl?: Record<string, number>; punktskatt?: Punktskattesats[] }) => api.patch('/inkop/installningar', data);
export const exporteraPrishistorik = (producentId?: string) =>
  api.get<Blob>('/inkop/prissattning/export', { params: producentId ? { producent_id: producentId } : {}, responseType: 'blob' });
export const låsPriser = (inkopId: string) => api.post(`/inkop/inkop/${inkopId}/las-priser`);
export const låsUppPriser = (inkopId: string) => api.post(`/inkop/inkop/${inkopId}/las-upp-priser`);
export const skapaPrisutkast = (producentId: string, data: Record<string, unknown>) => api.post(`/inkop/producenter/${producentId}/prisutkast`, data);
export const ändraPrisutkast = (id: string, data: Record<string, unknown>) => api.patch(`/inkop/prisutkast/${id}`, data);
export const taBortPrisutkast = (id: string) => api.delete(`/inkop/prisutkast/${id}`);

export interface Kostnad {
  id: string; typ: KostnadsTyp; belopp: number; valuta: string;
  forfallodag?: string | null; betald: boolean; betald_datum?: string | null; anteckning?: string | null; belopp_kr: number;
}

export interface Leverans {
  id: string;
  nummer: number;
  planerat_datum?: string | null;
  preliminart: boolean;
  status: LeveransStatus;
  inleverans_datum?: string | null;
  anteckning?: string | null;
  rader: { rad_id: string; antal: number }[];
  kostnader: Kostnad[];
  flaskor: number;
  pallar?: number | null;
  palltyp?: Palltyp | null;
  samfrakt?: string | null;
  flaskor_per_pall?: number | null;
  // Flaskor i leveransen när inköpets rader inte är kompletta (uppskattat).
  flaskor_totalt?: number | null;
  over_tak?: boolean;
}

// En sak som ska vara klar innan inköpet flyttas till nästa steg.
export interface Kontroll {
  nyckel: string; text: string; klar: boolean;
  // Bockad av sig själv, för att det syns i inköpet (t.ex. en betald betalning).
  automatisk: boolean; av?: string | null; tid?: string | null;
}

export interface Uppgift {
  id: string; inkop_id: string; leverans_id?: string | null; text: string;
  ansvarig?: string | null; forfaller?: string | null; klar: boolean; klar_av?: string | null; klar_tid?: string | null;
}

// Betalningarna läses från leverantörsfakturorna i Fortnox (beslut 27 okt 2026).
// Det som ännu inte fakturerats visas som en uppskattning från villkoren.
export interface Leverantörsfaktura {
  lopnummer: string; fakturanummer?: string | null; fakturadatum?: string | null; forfallodag?: string | null;
  total: number; saldo: number; valuta: string; total_kr: number; saldo_kr: number;
  betald: boolean; betald_datum?: string | null; bokford?: boolean; forfallen: boolean;
}
export interface Prognospost {
  beskrivning: string; belopp_valuta: number; belopp_kr?: number | null; forfallodag: string; preliminar: boolean; delvis: boolean;
}
export interface Betalningsläge {
  villkor: Villkor[];
  fakturor: Leverantörsfaktura[];
  prognos: Prognospost[];
  valuta: string;
  vantat_valuta: number; fakturerat_valuta: number; betalt_valuta: number; kvar_kr: number;
  forskott_betalt: boolean; allt_betalt: boolean; forfallet: boolean;
  // Har producenten någon leverantör i Fortnox kopplad?
  kopplad: boolean;
}

export interface InköpDetalj extends InköpKort {
  agent_andel: number;
  ls?: number | null;
  frakt_till_kund?: number | null;
  ovrigt?: number | null;
  kurs_datum?: string | null;
  kurs_egen?: boolean;
  anteckning?: string | null;
  import_kalla?: string | null;
  producent: {
    id: string; namn: string; land?: string; region?: string; valuta: string; betalningsvillkor: Villkor[];
    max_flaskor_per_pall?: number | null; adress?: string | null;
  };
  rader: Rad[];
  leveranser: Leverans[];
  betalning: Betalningsläge;
  standard: { ls: number; frakt_till_kund: number; ovrigt: number };
  lagerkostnad_kr: number;
  vinst_kr: number;
  // Den del som går till KRAN ApS och faktureras vidare: varor och sin andel av frakten.
  kran_aps: { flaskor: number; belopp_valuta: number; varor_kr: number; frakt_kr: number; totalt_kr: number } | null;
  uppgifter: Uppgift[];
  uppgiftsforslag: string[];
  kontroller: Partial<Record<InköpStatus, Kontroll[]>>;
  stegbeskrivning: Partial<Record<InköpStatus, string>>;
  villkor: Villkor[];
  egna_villkor: boolean;
  senaste_handelser: Händelse[];
  // När priserna låstes i Prissättning; tomt = inte låsta.
  priser_last?: string | null;
}

export const hämtaProducenter = () => api.get<Producent[]>('/inkop/producenter');
export const hämtaProducent = (id: string) => api.get<ProducentDetalj>(`/inkop/producenter/${id}`);
export const skapaProducent = (data: Partial<Producent> & { kategorier?: string[] }) => api.post<Producent>('/inkop/producenter', data);
export const ändraProducent = (id: string, data: Partial<Omit<Producent, 'kategorier'>> & { kategorier?: string[] }) =>
  api.patch(`/inkop/producenter/${id}`, data);

export const skapaKontakt = (producentId: string, data: Partial<Kontakt>) => api.post(`/inkop/producenter/${producentId}/kontakter`, data);
export const ändraKontakt = (id: string, data: Partial<Kontakt>) => api.patch(`/inkop/kontakter/${id}`, data);
export const taBortKontakt = (id: string) => api.delete(`/inkop/kontakter/${id}`);

export const skapaHändelse = (producentId: string, data: { typ: string; sammanfattning: string; rubrik?: string; datum?: string }) =>
  api.post(`/inkop/producenter/${producentId}/handelser`, data);
export const taBortHändelse = (id: string) => api.delete(`/inkop/handelser/${id}`);

export const bytSteg = (producentId: string, data: { steg: KandidatSteg; datum?: string; kommentar?: string; vilande_till?: string }) =>
  api.post<{ status: string; inkop_id?: string | null }>(`/inkop/producenter/${producentId}/steg`, data);
export const skapaProver = (producentId: string, data: { viner: { vin: string; argang?: string; antal_flaskor?: number | null }[]; mottaget?: string | null; anteckning?: string }) =>
  api.post<Prov[]>(`/inkop/producenter/${producentId}/prover`, data);
export const ändraProv = (id: string, data: Partial<Pick<Prov, 'vin' | 'argang' | 'antal_flaskor' | 'mottaget' | 'anteckning'>>) =>
  api.patch(`/inkop/prover/${id}`, data);
export const taBortProv = (id: string) => api.delete(`/inkop/prover/${id}`);
export const sparaOmdöme = (provId: string, data: { omdome: Omdömesval; kommentar?: string; datum?: string }) =>
  api.put(`/inkop/prover/${provId}/omdome`, data);
export const taBortOmdöme = (provId: string) => api.delete(`/inkop/prover/${provId}/omdome`);

// Startsidan: det som ska göras, pengar ut framåt, inköp som pågår och lager som tar slut.
export interface AttGöra {
  typ: 'betalning' | 'kostnad' | 'transport' | 'leverans' | 'prov' | 'beslut' | 'vilande' | 'uppgift' | 'deadline' | 'uppfoljning'
    | 'prissattning' | 'fortnox' | 'plan';
  grupp: 'forsenat' | 'veckan' | 'senare';
  datum?: string | null; titel: string; detalj: string; ansvarig?: string | null;
  belopp_valuta?: number | null; valuta?: string | null; belopp_kr?: number | null;
  uppgift_id?: string; lank: string;
}
export type StartInköp = InköpKort & { kandidat: boolean; lage: string };
export type Roll = 'producent' | 'logistik';
// Startsidan: vad den som är inloggad behöver göra nu, och pengar ut för hela bolaget.
export interface Startsida {
  person: Person | null;
  roller: Roll[];
  personer: (Person & { roller: Roll[] })[];
  admin: boolean;
  att_gora: AttGöra[];
  veckor: { vecka: number; start: string; nu: boolean; betalt: number; planerat: number; poster: string[] }[];
  betalt_bakat: number; planerat_framat: number; veckor_bakat: number; veckor_framat: number;
  pa_gang: StartInköp[] | null;
  pa_vag_in: StartInköp[] | null;
  kurser: Record<string, { kurs: number; datum: string }>;
  framat_30: number; framat_6m: number;
  steg: { status: InköpStatus; namn: string; inkop: StartInköp[] }[];
  kommande: { manad: string; namn: string; ar: number; totalt: number;
    poster: { producent_id: string; producent_namn: string | null; inkop_id: string | null; kategori: PlanKategori | null; sakerhet: Säkerhet; statusnamn: string | null; belopp_kr: number | null }[] }[];
  lager: { artikel_nr: string; namn: string; producent: string; producent_id: string; lager: number; inkop: string | null; planerat: string | null }[];
  lagergrans: number;
  // Översikten (3 nov 2026)
  lager_antal: number;
  lager_att_planera: Startsida['lager'];
  lager_att_planera_antal: number;
  forfallet: number;
  nasta_betalning: { datum: string; kr: number; text: string; uppskattat: boolean } | null;
}
export const hämtaStartsida = (person?: string) =>
  api.get<Startsida>('/inkop/startsida', { params: person ? { person } : {} });

export const hämtaKategorier = () => api.get<Kategori[]>('/inkop/kategorier');
export const skapaKategori = (data: { namn: string; farg?: string }) => api.post<Kategori>('/inkop/kategorier', data);
export const hämtaInköpsanvändare = () => api.get<Person[]>('/inkop/anvandare');

export const hämtaInköp = (status?: string) => api.get<InköpKort[]>('/inkop/inkop', { params: status ? { status } : {} });
export const hämtaEttInköp = (id: string) => api.get<InköpDetalj>(`/inkop/inkop/${id}`);
export const skapaInköp = (data: { producent_id: string; rubrik?: string; kurs?: number }) => api.post<InköpKort>('/inkop/inkop', data);
export const ändraInköp = (id: string, data: Record<string, unknown>) => api.patch(`/inkop/inkop/${id}`, data);
export const taBortInköp = (id: string) => api.delete(`/inkop/inkop/${id}`);

export const skapaRad = (inkopId: string, data: Record<string, unknown>) => api.post(`/inkop/inkop/${inkopId}/rader`, data);
export const ändraRad = (id: string, data: Record<string, unknown>) => api.patch(`/inkop/rader/${id}`, data);
export const taBortRad = (id: string) => api.delete(`/inkop/rader/${id}`);

export const skapaLeverans = (inkopId: string, data: Record<string, unknown> = {}) => api.post(`/inkop/inkop/${inkopId}/leveranser`, data);
export const ändraLeverans = (id: string, data: Record<string, unknown>) => api.patch(`/inkop/leveranser/${id}`, data);
export const taBortLeverans = (id: string) => api.delete(`/inkop/leveranser/${id}`);
export const skapaKostnad = (leveransId: string, data: Record<string, unknown>) => api.post(`/inkop/leveranser/${leveransId}/kostnader`, data);
export const ändraKostnad = (id: string, data: Record<string, unknown>) => api.patch(`/inkop/kostnader/${id}`, data);
export const taBortKostnad = (id: string) => api.delete(`/inkop/kostnader/${id}`);

export const skapaUppgift = (inkopId: string, data: Partial<Uppgift>) => api.post<Uppgift>(`/inkop/inkop/${inkopId}/uppgifter`, data);
export const ändraUppgift = (id: string, data: Partial<Uppgift>) => api.patch(`/inkop/uppgifter/${id}`, data);
export const taBortUppgift = (id: string) => api.delete(`/inkop/uppgifter/${id}`);
export const slåIhop = (inkopId: string, fran: string) => api.post(`/inkop/inkop/${inkopId}/sla-ihop`, { fran });
export const bockaKontroll = (inkopId: string, nyckel: string, klar: boolean) =>
  api.post(`/inkop/inkop/${inkopId}/kontroller`, { nyckel, klar });

// Formatering som används på alla sidor i modulen.
// Att åtgärda: det som inte kunde matchas säkert. Se backend/routers/inkop_atgarda.py.
export interface Producentförslag { id: string; namn: string; poäng: number }
export interface TillverkarePunkt {
  nyckel: string; tillverkare: string; antal_artiklar: number;
  artiklar: { artikel_nr: string; beskrivning: string; lager: number }[];
  forslag: Producentförslag | null;
}
export interface Artikelkandidat {
  artikel_nr: string; namn: string; pris: number; poäng: number; ny_årgång?: boolean;
  lager?: number | null; annan_argang?: boolean; samma_argang?: boolean; artikel_årgång?: string | null;
}
export interface RadPunkt {
  nyckel: string; rad_id: string; inkop_id: string; inkop: string; producent_id: string;
  vin: string; argang?: string | null; volym_cl?: number; antal?: number; listpris?: number | null;
  angivet_artikel_nr?: string | null; bland_producentens: boolean; kandidater: Artikelkandidat[];
  // Två eller fler artiklar i Fortnox som ser ut att vara samma vin.
  dubbletter: string[];
  // Var inköpet kommer ifrån, t.ex. prismodellen i Excel.
  kalla?: string | null; bestalld?: string | null;
}
export interface DubblettInköp {
  id: string; rubrik?: string | null; statusnamn: string; bestalld?: string | null; flaskor: number;
  belopp_valuta: number; valuta: string; importerat?: boolean; import_kalla?: string | null; skapad?: string;
}
export interface DubblettPunkt { nyckel: string; producent_id: string; producent: string; inkop: DubblettInköp[] }
export interface Avvikelse {
  id: string; nyckel: string; typ: 'import' | 'tillverkare' | 'rad' | 'dubblett' | 'leverantor'; text: string;
  status: 'oppen' | 'hanterad' | 'ignorerad'; kommentar?: string | null; skapad?: string;
  hanterad_tid?: string | null; hanterad_av_namn?: string | null; inkop_id?: string | null; producent_id?: string | null;
  // data.auto: kopplat automatiskt enligt reglerna i matchning.py; kan ångras.
  data?: { auto?: boolean; [k: string]: unknown };
}
// En leverantör i Fortnox utan producent, som liknar en producent eller fakturerar i utländsk valuta.
export interface LeverantörPunkt {
  nyckel: string; leverantor_nr: string; namn?: string | null; antal_fakturor: number; valutor: string[];
  senaste?: string | null; forslag: { id: string; namn: string; poäng: number } | null;
}
// Ett vin vars låsta pris skiljer sig från listpriset i Fortnox. Fortnox gäller.
export interface PrisavvikelsePunkt {
  nyckel: string; rad_id: string; inkop_id: string; producent_id: string; producent: string; vin?: string | null; argang?: string | null;
  artikel_nr?: string | null; last_listpris: number; fortnox_listpris: number; last_marginal?: number | null; fortnox_marginal?: number | null;
  bestalld?: string | null;
}
export interface AttÅtgärda {
  tillverkare: TillverkarePunkt[]; rader: RadPunkt[]; dubbletter: DubblettPunkt[]; import: Avvikelse[];
  leverantorer: LeverantörPunkt[];
  automatiskt: Avvikelse[];
  prisavvikelser: PrisavvikelsePunkt[];
  // Resultatet av automatiken när sidan hämtades.
  automatik?: { tillverkare: number; rader: number; fel?: string[] };
  besvarade: Avvikelse[]; antal: number;
}
export const hämtaAttÅtgärda = () => api.get<AttÅtgärda>('/inkop/atgarda');
export const hämtaAntalAttÅtgärda = () => api.get<{ antal: number }>('/inkop/atgarda/antal');
export const kopplaTillverkare = (tillverkare: string, producent_id: string) =>
  api.post('/inkop/atgarda/tillverkare/koppla', { tillverkare, producent_id });
export const ignoreraTillverkare = (tillverkare: string, kommentar?: string) =>
  api.post('/inkop/atgarda/tillverkare/ignorera', { tillverkare, kommentar });
export const kopplaRad = (radId: string, artikel_nr: string) => api.post(`/inkop/atgarda/rader/${radId}/koppla`, { artikel_nr });
export const inteSammaRad = (radId: string, artikel_nr: string) => api.post(`/inkop/atgarda/rader/${radId}/inte-samma`, { artikel_nr });
export const ingenArtikel = (radId: string, kommentar?: string) => api.post(`/inkop/atgarda/rader/${radId}/ingen-artikel`, { kommentar });
export const inteDubblett = (a: string, b: string, kommentar?: string) => api.post('/inkop/atgarda/dubbletter/inte-samma', { a, b, kommentar });
export const kopplaLeverantör = (leverantor_nr: string, namn: string | null | undefined, producent_id: string) =>
  api.post('/inkop/atgarda/leverantorer/koppla', { leverantor_nr, namn, producent_id });
export const ignoreraLeverantör = (leverantor_nr: string, namn?: string | null, kommentar?: string) =>
  api.post('/inkop/atgarda/leverantorer/ignorera', { leverantor_nr, namn, kommentar });
export const kopplaLossLeverantör = (leverantor_nr: string, producent_id: string) =>
  api.post('/inkop/atgarda/leverantorer/koppla-loss', { leverantor_nr, producent_id });
export const ångraAutomatisk = (id: string, kommentar?: string) =>
  api.post(`/inkop/atgarda/automatiskt/${id}/angra`, { kommentar });
export const prisavvikelseSett = (nyckel: string, rad_id: string, kommentar?: string) =>
  api.post('/inkop/atgarda/prisavvikelser/sett', { nyckel, rad_id, kommentar });
export const svaraAvvikelse = (id: string, status: Avvikelse['status'], kommentar?: string) =>
  api.post(`/inkop/atgarda/avvikelser/${id}`, { status, kommentar });

export const kr = (v?: number | null, dec = 0) =>
  v == null ? '–' : `${v.toLocaleString('sv-SE', { minimumFractionDigits: dec, maximumFractionDigits: dec })} kr`;
export const valutabelopp = (v: number | null | undefined, valuta: string, dec = 2) =>
  v == null ? '–' : `${v.toLocaleString('sv-SE', { minimumFractionDigits: dec, maximumFractionDigits: dec })} ${valuta === 'EUR' ? '€' : valuta === 'USD' ? '$' : valuta}`;
export const idag = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const MÅNADER = ['jan', 'feb', 'mars', 'apr', 'maj', 'juni', 'juli', 'aug', 'sep', 'okt', 'nov', 'dec'];
// "21 maj 2026". Med kort = true utelämnas året när det är i år: "26 okt".
export const datumText = (d?: string | null, kort = false) => {
  if (!d) return '–';
  const [år, mån, dag] = d.slice(0, 10).split('-').map(Number);
  if (!år || !mån || !dag) return '–';
  const iÅr = år === new Date().getFullYear();
  return `${dag} ${MÅNADER[mån - 1]}${kort && iÅr ? '' : ` ${år}`}`;
};
export const felText = (err: any, reserv: string): string => err?.response?.data?.detail || reserv;

// ── Inköpsplanen ────────────────────────────────────────────────────────────
// Tolv månader framåt. Backend: backend/routers/inkopsplan.py.
export type PlanKategori = 'glasvin' | 'lansering' | 'unicorn';
export type Säkerhet = 'bestallt' | 'dialog' | 'planerat';
export interface PlanPost {
  typ: 'plan' | 'inkop';
  id: string;
  inkop_id: string | null;
  post_id: string | null;
  producent_id: string;
  producent_namn: string | null;
  kandidat: boolean;
  manad: string;
  sen: boolean;
  utan_datum?: boolean;
  har_hamtning?: boolean;
  planerad_manad?: string;
  kategori: PlanKategori | null;
  sakerhet: Säkerhet;
  status: InköpStatus | null;
  statusnamn: string | null;
  belopp_kr: number | null;
  uppskattat: boolean;
  valuta: string | null;
  belopp_valuta: number | null;
  flaskor: number | null;
  text: string | null;
}
export interface PlanMånad {
  manad: string; namn: string; ar: number; nu: boolean;
  poster: PlanPost[];
  summa: Record<Säkerhet, number>;
  totalt: number;
  per_kategori: Record<PlanKategori, number>;
  utan_belopp: number;
}
export interface PlanProducent {
  id: string; namn: string; land?: string | null; kandidat: boolean;
  forslag_kr: number | null; senaste: string | null; kategori: PlanKategori | null;
}
export interface Inköpsplan {
  manader: PlanMånad[];
  aldre: PlanPost[];
  aldre_totalt: number;
  kategorier: { id: PlanKategori; namn: string }[];
  producenter: PlanProducent[];
  totalt: number;
  per_kategori: Record<PlanKategori, number>;
  per_sakerhet: Record<Säkerhet, number>;
}
export interface NyPlanPost { producent_id?: string; manad?: string; kategori?: PlanKategori | null; belopp_ca?: number | null; text?: string | null }
export const hämtaInköpsplan = () => api.get<Inköpsplan>('/inkop/plan');
export const skapaPlanPost = (data: NyPlanPost) => api.post('/inkop/plan', data);
export const ändraPlanPost = (id: string, data: NyPlanPost) => api.patch(`/inkop/plan/${id}`, data);
export const taBortPlanPost = (id: string) => api.delete(`/inkop/plan/${id}`);
export const startaInköpFrånPlan = (id: string) => api.post<{ id: string; producent_id: string }>(`/inkop/plan/${id}/starta`);
export const ändraInköpIPlanen = (inkopId: string, data: { kategori?: PlanKategori | null; manad?: string | null }) =>
  api.patch(`/inkop/plan/inkop/${inkopId}`, data);
export const sättInköpetsKategori = (inkopId: string, kategori: PlanKategori | null) => ändraInköpIPlanen(inkopId, { kategori });
